/**
 * Which time zones count as "in the UK", and the predicate both clients use.
 *
 * ONE list, in shared code, imported by `client/src/lib/outside-uk-notice.ts`
 * and `mobile/src/lib/outside-uk-notice.ts`. The two notice modules are
 * otherwise separate implementations of the same rule, held together by a
 * comment saying they are written the same way — and a comment is not an
 * enforcement. A list is exactly the thing that drifts: someone adds a zone
 * where the bug was reported and not to the other copy, and the platform they
 * did not touch keeps telling a British customer her own country is
 * unsupported. That failure is the reason this file exists rather than two
 * constants.
 *
 * ## The list, and why each is on it
 *
 * `Europe/London`      the canonical UK zone.
 *
 * `Europe/Jersey`      Crown Dependencies. Not the UK constitutionally, but
 * `Europe/Guernsey`    they pay in sterling and UK retailers ship to them as a
 * `Europe/Isle_of_Man` matter of course, so the notice's "ordering from
 *                      elsewhere can be tricky or expensive" does not describe
 *                      their experience. These are the three that prompted
 *                      this change: they are the only UK-ish names that ICU
 *                      does NOT fold into Europe/London, so under the old
 *                      `!== 'Europe/London'` rule they saw the notice.
 *
 * `Europe/Belfast`     Legacy aliases, and NOT defensive extras. Measured on
 * `GB`                 the platform that ships: an iOS simulator running this
 * `GB-Eire`            code returns all three VERBATIM, unfolded.
 *
 *                        set=Europe/Belfast -> zone=Europe/Belfast  isUK=true
 *                        set=GB             -> zone=GB              isUK=true
 *                        set=GB-Eire        -> zone=GB-Eire         isUK=true
 *
 *                      An earlier version of this comment said they resolve to
 *                      `Europe/London` and "should never reach us". That was
 *                      measured on NODE's ICU, not the one the app runs on,
 *                      and it is false where it counts. These three entries
 *                      are the difference between a Belfast device seeing the
 *                      notice and not. Do not delete them as redundant.
 *
 *                      Not established: whether iOS's own picker can put a
 *                      device on these names, since it is city-based. The
 *                      entries stand either way, because if such a value does
 *                      arrive they are what prevents the defect.
 *
 * ## Not on the list: Europe/Dublin
 *
 * Ireland is a separate country, and since Brexit an order from a UK retailer
 * to Dublin can carry a customs declaration and duty. "Tricky or expensive" is
 * MORE accurate there than in Jersey, not less. The Crown Dependencies are
 * here because sterling and UK shipping make the notice wrong for them; none
 * of that applies to Ireland.
 *
 * ## Case-insensitive, and why
 *
 * Comparison is on a lowercased copy. Measured on this project's Node ICU,
 * `resolvedOptions().timeZone` always answers in canonical case, even when the
 * zone was requested as `europe/london` — and a `TZ` env var whose case does
 * not match returns `undefined` rather than a lowercase string. So I have NO
 * evidence any engine we ship to returns a non-canonical name, and this guards
 * a case I could not produce.
 *
 * It is still the right default, because the errors are not symmetric. Folding
 * case can only ever ADD names to the "in the UK" set, so it can only move a
 * mistake in the safe direction: the worst it can do is fail to show the
 * notice to somebody it would otherwise have shown it to, and the thing being
 * avoided is telling a British customer her country is unsupported. A
 * `toLowerCase()` on a short string once per launch is not a cost worth
 * trading against that.
 *
 * THE FOLD MUST STAY LOCALE-INDEPENDENT, and that is the property the argument
 * above quietly rests on. `toLowerCase()` is; `toLocaleLowerCase()` is not.
 * Swapping one for the other leaves every test in this suite green, and on a
 * Turkish-locale device it maps `I` to dotless `ı`, so `Europe/Isle_of_Man`
 * folds to `europe/ısle_of_man`, misses the set, and the customer in Douglas
 * sees the notice. That is the exact defect this file exists to remove,
 * reintroduced by one word.
 *
 * Deliberately not pinned by a test: reproducing it needs a process-level
 * `LANG`, which makes the test fragile enough to be its own liability. The
 * constraint lives here instead. Do not "modernise" this call.
 */

/**
 * The zones that mean "in the UK", in canonical form for reading.
 *
 * Exported so a test can walk the real list rather than restating it, which is
 * what lets a dropped entry fail by name.
 */
export const UK_TIMEZONES = [
  'Europe/London',
  'Europe/Jersey',
  'Europe/Guernsey',
  'Europe/Isle_of_Man',
  'Europe/Belfast',
  'GB',
  'GB-Eire',
] as const;

/** The canonical one, for callers that need a single representative value. */
export const UK_TIMEZONE = 'Europe/London';

/** Lowercased once at module scope; the comparison set. */
const UK_TIMEZONE_KEYS: ReadonlySet<string> = new Set(
  UK_TIMEZONES.map((zone) => zone.toLowerCase()),
);

/** True when this zone name is one we treat as the UK. */
export function isUKTimeZone(zone: string): boolean {
  return UK_TIMEZONE_KEYS.has(zone.trim().toLowerCase());
}

/** Reads the device's zone. Injectable so tests can make it fail. */
export type TimeZoneReader = () => string | undefined;

const defaultReader: TimeZoneReader = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * True only when we READ a zone and it is not a UK one.
 *
 * Every other outcome is false: no `Intl`, a throw, an empty string, or a zone
 * on the list. "We could not tell" and "she is in the UK" lead to the same
 * action, and neither is evidence of being abroad.
 *
 * It fails CLOSED, and the direction is the whole point. A false negative is a
 * customer who does not see a notice. A false positive tells a customer in
 * London, or now in Douglas, that her own country is unsupported.
 */
export function isOutsideUK(read: TimeZoneReader = defaultReader): boolean {
  try {
    const zone = read();
    if (typeof zone !== 'string' || zone.trim() === '') return false;
    return !isUKTimeZone(zone);
  } catch {
    return false;
  }
}
